import { useDispatch } from 'react-redux';

import type { MediaOptions } from 'js/src/client';
import type { AccountState } from 'js/src/data/account/account-reducers';
import type { AiVoicesAndAvatarsState } from 'js/src/data/ai-voices-and-avatars/ai-voices-and-avatars-reducers';
import type { AnalyticsState } from 'js/src/data/analytics/analytics-reducers';
import type { AnimatedExplainerVideosState } from 'js/src/data/animated-explainer-videos/genx-reducers';
import type { AnnouncementsState } from 'js/src/data/announcements/announcement-reducers';
import type { ApiSettingsState } from 'js/src/data/api-settings/api-settings-slice';
import type { AppState } from 'js/src/data/app/app-reducers';
import type { ArticleState } from 'js/src/data/article/article-slice';
import type { AudioEnhancementState } from 'js/src/data/audio-enhancement/audio-enhancement-reducers';
import type { auditLogState } from 'js/src/data/audit-log/audit-log-slice';
import type { BlueprintsState } from 'js/src/data/blueprints/blueprints-reducers';
import type { BrandEnvironmentsState } from 'js/src/data/brand-environments/brand-environments-slice';
import type { BrandKitsState } from 'js/src/data/brand-kits/brand-kits-reducers';
import type { BrandKitsEditorState } from 'js/src/data/brand-kits-editor/brand-kits-editor-reducers';
import type { ClipCreatorState } from 'js/src/data/clip-creator/clip-creator-reducers';
import type { CodeoState } from 'js/src/data/codeo/codeoSlice';
import type { ColorGradingState } from 'js/src/data/color-grading/color-grading-slice';
import type { CommentsState } from 'js/src/data/comments/comments-reducer';
import type { DashboardState } from 'js/src/data/dashboard/dashboard-reducers';
import type { DocumentState } from 'js/src/data/document/document-reducers';
import type { FontsState } from 'js/src/data/fonts/fonts-reducers';
import type { HtmlDesignState } from 'js/src/data/html-design';
import type { MediaState } from 'js/src/data/media/media-reducers';
import type { ModalsState } from 'js/src/data/modals/modals-reducers';
import type { NotificationsState } from 'js/src/data/notifications/notification-reducers';
import type { PremiumMediaState } from 'js/src/data/premium-media/premium-media-reducers';
import type { PricingState } from 'js/src/data/pricing/pricing-slice';
import type { PronunciationsState } from 'js/src/data/pronunciations/pronunciations-reducer';
import type { RecentlyUsedTemplatesState } from 'js/src/data/recently-used-templates/recently-used-templates-reducers';
import type { RecommendationsState } from 'js/src/data/recommendations/recommendations-reducers';
import type { AgentActivityState } from 'js/src/data/agent-activity/agent-activity-reducers';
import type { ScenesState } from 'js/src/data/scenes/scenes-reducers';
import type { ScriptComposerState } from 'js/src/data/script-composer/script-composer-reducers';
import type { ShareVideoState } from 'js/src/data/share-video/share-video-reducers';
import type { SketchControlsState } from 'js/src/data/sketch-controls/sketch-controls-slice';
import type { SoundEffectsState } from 'js/src/data/sound-effects/sound-effects-slice';
import type { TemplatesState } from 'js/src/data/templates/templates-reducers';
import type { ThumbnailPreviewsState } from 'js/src/data/thumbnail-previews/thumbnail-previews-reducers';
import type { TranscriptState } from 'js/src/data/transcript/transcript-reducers';
import type { TranslatedEditorsState } from 'js/src/data/translated-editors/translated-editors-reducers';
import type { TranslationsState } from 'js/src/data/translations/translations-reducers';
import type { UploadsState } from 'js/src/data/uploads/uploads-reducers';
import type { usageLimitState } from 'js/src/data/usage-limit/usage-limit-slice';
import type { VideoContentInputState } from 'js/src/data/video-content-input';
import type { VideoPlannerState } from 'js/src/data/video-planner/video-planner-slice';
import type { EditorAppState } from 'js/src/editor/editor-app-reducer';
import type { SceneDesignEditorState } from 'js/src/editor/scene-design/scene-design-editor-reducer';
import type { UndoableState } from 'js/src/entry-points/root-reducers/reducers/undoable';
import type { createReduxStore } from 'js/src/libs/redux-utils';

/**
 * This contains all the top level reducers. Due to the nature of our entry-points, different pages
 * will contain a different subset of this RootState. For example, in share-video pages, we may only
 * have account/shareVideo/comments/notifications reducers, and not app or dashboard.
 * However, you can still use this RootState type to help define your top level selectors, where TS
 * is still able to derive the slice of the state you're selecting.
 */
export type RootState = {
    aiVoicesAndAvatars: AiVoicesAndAvatarsState;
    animated_explainer_videos: AnimatedExplainerVideosState;
    announcements: AnnouncementsState;
    article: ArticleState;
    document: DocumentState;
    app: AppState;
    account: AccountState;
    analytics: AnalyticsState;
    blueprints: BlueprintsState;
    brandEnvironments?: BrandEnvironmentsState;
    brandKits: BrandKitsState;
    brandKitsEditor: BrandKitsEditorState;
    dashboard: DashboardState;
    notifications: NotificationsState;
    modals: ModalsState;
    thumbnailPreviews: ThumbnailPreviewsState;
    shareVideo: ShareVideoState;
    comments: CommentsState;
    translations: TranslationsState;
    scenes: ScenesState;
    agentActivity?: AgentActivityState;
    uploads: UploadsState;
    recentlyUsedTemplates: RecentlyUsedTemplatesState;
    undoable: UndoableState;
    media: MediaState;
    sketchControls?: SketchControlsState;
    premium_media: PremiumMediaState;
    templates: TemplatesState;
    fonts: FontsState;
    transcript_translations: TranslatedEditorsState;
    transcript: TranscriptState;
    script_composer: ScriptComposerState;
    audio_enhancement: AudioEnhancementState;
    recommendations: RecommendationsState;
    mediaRecommendationPreferences: MediaOptions;
    pronunciations: PronunciationsState;
    usageLimit: usageLimitState;
    auditLog: auditLogState;
    apiSettings: ApiSettingsState;
    clip_creator: ClipCreatorState;
    codeo: CodeoState;
    htmlDesign: HtmlDesignState;
    colorGrading: ColorGradingState;
    soundEffects: SoundEffectsState;
    videoContentInput: VideoContentInputState;
    videoPlanner: VideoPlannerState;
    pricing: PricingState;
    editorApp: EditorAppState;
    sceneDesignEditor: SceneDesignEditorState;
};

/**
 * Ideally, we would define a new type AppDispatch, which has a computed type from the
 * reduxStore (like this: https://redux.js.org/usage/usage-with-typescript#define-typed-hooks)
 *
 * But since we don't have a single store, we have different ones for different pages, we need
 * just use the generic ThunkDispatch type.
 */
export type AppDispatch = ReturnType<typeof createReduxStore>['dispatch'];

/**
 * The counterpart to AppDispatch: the type of the store's `getState`, which luminary
 * operations receive alongside `dispatch`.
 */
export type GetRootState = () => RootState;

/**
 * useAppDispatch will help typescript know that `dispatch` can be used with redux-thunks.
 * It will make sure that the call to `dispatch(myThunk(params))` has the correct return type.
 * To use it, make sure that you are using the `useAppDispatch` hook instead of `useDispatch` hook
 * inside of your component:
 *
 * function MyComponent(props) {
 *  const dispatch = useAppDispatch(); // NOTE: useAppDispatch, not useDispatch!
 *
 *  useEffect(() => {
 *    const correctReturnType = dispatch(myThunk(params));
 *    // do something with correctReturnType
 *  }, []);
 * }
 *
 *
 * More info can be found here: https://redux.js.org/usage/usage-with-typescript#define-root-state-and-dispatch-types
 */
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
